import { PlusCircleIcon } from '@phosphor-icons/react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { FormControl, FormField } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { GenericTable, Spinner } from '@/modules/common';
import { Mask } from '@/modules/masks/types';

import { AddEditMaskDialog } from './components/add-mask-dialog';
import { useMasksTab } from './hooks/use-masks-tab';

export const defaultMasks = [
  {
    maskId: '',
    overlayId: '',
  },
];

export interface MaskColumn extends Mask {
  overlay: string;
  overlayImageUrl: string;
}

const masksFields = [
  {
    label: 'Flags & Initials',
    formKey: 'flagsAndInitialsEnabled',
  },
  {
    label: 'Autograph',
    formKey: 'signatureEnabled',
  },
];

export function MasksTab() {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedMask, setSelectedMask] = useState<MaskColumn | null>(null);

  const {
    control,
    columns,
    fields,
    isFetching,
    filteredMasksData,
    filteredOverlaysData,
    formattedMasks,
    onAdd,
    onEdit,
  } = useMasksTab();
  return (
    <>
      {isFetching ? (
        <Spinner />
      ) : (
        <>
          <div className="flex flex-col gap-5">
            <div className="border rounded-lg border-zinc-800 p-6">
              <h3 className="text-lg font-semibold mb-4">Models</h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:grid-cols-2 grid-flow-col grid-rows-6 sm:grid-rows-1">
                {masksFields.map(({ label, formKey }) => (
                  <div className="gap-3 flex flex-col" key={formKey}>
                    <h3 className="text-sm font-semibold text-zinc-50">
                      {label}
                    </h3>
                    <FormField
                      control={control}
                      name={formKey}
                      render={({ field }) => (
                        <FormControl>
                          <div className="border p-2 h-[44px] border-zinc-800 rounded-md">
                            <label
                              htmlFor={formKey}
                              className="cursor-pointer flex items-center gap-2 text-zinc-100"
                            >
                              <span
                                className={cn(
                                  'text-sm mr-auto',
                                  field.value ? 'text-zinc-50' : 'text-zinc-400'
                                )}
                              >
                                {field.value ? 'Enable' : 'Disable'}
                              </span>
                              <Switch
                                id={formKey}
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </label>
                          </div>
                        </FormControl>
                      )}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Masks Table</h3>
                <Button
                  disabled={isFetching}
                  data-testid="add-model-button"
                  onClick={() => setAddDialogOpen(true)}
                  startIcon={<PlusCircleIcon size={16} />}
                >
                  Add Mask
                </Button>
              </div>
              <GenericTable
                name="masks"
                columns={columns}
                key="masks-table"
                data={formattedMasks}
                emptyCellClassName="h-20"
                tableCellClassName="py-1"
                tableRowClassName="hover:bg-transparent"
                disabledColumnsIdsToClick={['name', 'overlay', 'actions']}
                onClick={(mask) => {
                  setSelectedMask(mask);
                  setAddDialogOpen(true);
                }}
                emptyTitle="No masks yet"
                emptyDescription="Start building your masks."
                emptyButton={
                  <Button
                    data-testid="add-model-button"
                    onClick={() => setAddDialogOpen(true)}
                    startIcon={<PlusCircleIcon size={16} />}
                  >
                    Add Mask
                  </Button>
                }
              >
                <p className="text-zinc-400 text-sm">
                  All masks: {fields.length}
                </p>
              </GenericTable>
            </div>
          </div>
          <AddEditMaskDialog
            isOpen={addDialogOpen}
            onOpenChange={(open) => {
              setAddDialogOpen(open);
              setSelectedMask(null);
            }}
            masksData={filteredMasksData}
            overlayData={filteredOverlaysData}
            selectedMask={selectedMask}
            onAdd={onAdd}
            onEdit={(mask) => onEdit(mask, selectedMask!)}
          />
        </>
      )}
    </>
  );
}
